import { Spacer } from "@hackthenorth/north";
import { Button, Card, Text } from "north.js";
import React, { useState } from "react";
import { useNavigate } from "react-router";
import styled from "styled-components";

import {
  Dropzone,
  DropzoneError,
  FileLink,
  Header,
  Icon,
  PageWrapper,
} from "../../../../../src/shared/components";
import {
  errorToast,
  successToast,
} from "../../../../../src/shared/components/Toast";
import { useUploadFile } from "../../../../../src/shared/hooks/useUploadFile";
import { HackerAPIFile } from "../../../../../src/shared/utils/hackerapi";
import globalConstants from "../../../../../src/theme/globalConstants";
import { CURRENT_EVENT_SLUG } from "../../../../shared/constants/event";
import { useSeedVolunteerShiftsMutation } from "../graphql/seedVolunteerShift.generated";

const uploadErrorMessage = (err: unknown) => {
  const graphQLMessage = (
    err as {
      graphQLErrors?: { message?: string }[];
    }
  )?.graphQLErrors?.[0]?.message;
  if (graphQLMessage) return graphQLMessage;
  if (err instanceof Error && err.message) return err.message;
  return "Couldn't import that spreadsheet. Check the CSV and try again.";
};

const UploadSpreadsheet: React.FC = () => {
  const [file, setFile] = useState<HackerAPIFile | null>(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const onBack = () => {
    navigate("/volunteer/shifts");
  };

  const { uploadFile } = useUploadFile();

  const addFile = async (files: File[]) => {
    if (files.length === 1) {
      const uploaded = files[0];
      const hapiFile = await uploadFile(uploaded);

      if (hapiFile) setFile(hapiFile);
    }
  };

  const [seedVolunteerShifts, { loading }] = useSeedVolunteerShiftsMutation();

  const handleSubmit = async () => {
    if (!file?.uri || loading) return;
    try {
      await seedVolunteerShifts({
        variables: {
          url: file.uri,
          event_slug: CURRENT_EVENT_SLUG,
        },
      });
      successToast("Shifts imported");
      setFile(null);
      navigate("/volunteer/shifts");
    } catch (err) {
      errorToast(uploadErrorMessage(err));
    }
  };

  return (
    <>
      <Header
        title="Upload Spreadsheet"
        subtitle="Upload a spreadsheet to update volunteer shifts"
      />
      <PageWrapper>
        <BackButton onClick={onBack}>
          <Icon
            name="chevron-left"
            color={globalConstants?.color?.textSecondary as string}
            style={{ width: "24px", height: "24px" }}
          />
          <BackText>Back to Shifts</BackText>
        </BackButton>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            marginTop: "16px",
            marginBottom: "48px",
            gap: "16px",
          }}
        >
          <Text type="h2">Upload Spreadsheet</Text>
          <Text type="body1">
            Add, edit, or delete items by uploading a spreadsheet or updating
            each item individually.
          </Text>
          <Dropzone
            max={1}
            onError={(error: DropzoneError) => {
              setError(error);
            }}
            acceptedFileTypes={["csv"]}
            label={
              <span>
                <b>Upload a file</b> or drag and drop
              </span>
            }
            onFilesUpload={addFile}
            onBlur={() => setError("")}
          />
          {error && <Card>{error}</Card>}
          {file && (
            <>
              <Spacer height={8} />
              <FileLink file={file} />
            </>
          )}
          <Button
            color="primary"
            disabled={!file || loading}
            onClick={handleSubmit}
          >
            {loading ? "Submitting..." : "Submit"}
          </Button>
        </div>
      </PageWrapper>
    </>
  );
};

const BackButton = styled.button`
  display: flex;
  padding: 6px 0px;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;

const BackText = styled.button`
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
  color: ${globalConstants?.color?.textSecondary as string};
`;

export default UploadSpreadsheet;
